import type { FocalPoint } from '../types/focal-point';

/**
 * Calculate the scale and position for focal point cropping.
 * 
 * The focal point defines a crop area within the rendition. We need to:
 * 1. Scale the image so the focal crop area fills the container
 * 2. Position the image so the focal centroid is centered
 *
 * @param imageWidth - Width of the image rendition
 * @param imageHeight - Height of the image rendition
 * @param focalPoint - Focal point data with crop dimensions and centroid
 * @returns CSS object with transform and object-position
 */
export function getFocalPointStyle(
  imageWidth: number,
  imageHeight: number,
  focalPoint: FocalPoint
): { transform: string; objectPosition: string; transformOrigin: string } {
  // Calculate how much we need to scale the rendition so the focal crop fills the container
  // The container will use object-fit: cover, which means it will fill to the smaller dimension
  // We need to scale UP so that the focal crop area matches the rendition size
  const scaleX = imageWidth / focalPoint.width;
  const scaleY = imageHeight / focalPoint.height;
  const scale = Math.max(scaleX, scaleY); // Use max to ensure full coverage
  
  // Calculate the position of the centroid as a percentage of the rendition
  const xPercent = (focalPoint.centroid_x / imageWidth) * 100;
  const yPercent = (focalPoint.centroid_y / imageHeight) * 100;
  
  return {
    transform: `scale(${scale})`,
    objectPosition: `${xPercent}% ${yPercent}%`,
    transformOrigin: `${xPercent}% ${yPercent}%`, // Scale from the focal point
  };
}

/**
 * Legacy: Calculate object-position CSS value to center the focal point.
 * 
 * @deprecated Use getFocalPointStyle instead for proper focal crop scaling
 */
export function getFocalPointPosition(
  imageWidth: number,
  imageHeight: number,
  focalPoint: FocalPoint
): string {
  const xPercent = (focalPoint.centroid_x / imageWidth) * 100;
  const yPercent = (focalPoint.centroid_y / imageHeight) * 100;
  
  return `${xPercent}% ${yPercent}%`;
}

/**
 * Legacy function for backwards compatibility.
 * Calculate the X-axis translation percentage for focal point positioning.
 *
 * @deprecated Use getFocalPointStyle instead
 */
export function transX(origW: number, x: number): string {
  const halfW = origW / 2;
  if (x > halfW) {
    return `${-((origW - x) / origW) * 100}%`;
  } else if (x === halfW) {
    return '0%';
  } else {
    return `${((halfW - x) / origW) * 100}%`;
  }
}

/**
 * Legacy function for backwards compatibility.
 * Calculate the Y-axis translation percentage for focal point positioning.
 *
 * @deprecated Use getFocalPointStyle instead
 */
export function transY(origH: number, y: number): string {
  const halfH = origH / 2;
  if (y > halfH) {
    return `${-((origH - y) / origH) * 100}%`;
  } else if (y === halfH) {
    return '0%';
  } else {
    return `${((halfH - y) / origH) * 100}%`;
  }
}

/**
 * Legacy function for backwards compatibility.
 * Calculate the scale factor for focal point positioning.
 *
 * @deprecated Use getFocalPointStyle instead
 */
export function calculateScale(
  imageWidth: number,
  imageHeight: number,
  focalPoint: FocalPoint
): number {
  const scaleX = imageWidth / focalPoint.width;
  const scaleY = imageHeight / focalPoint.height;
  return (scaleX + scaleY) / 2;
}

/**
 * Legacy function for backwards compatibility.
 * Generate the complete CSS transform string for focal point positioning.
 *
 * @deprecated Use getFocalPointStyle instead
 */
export function getFocalPointTransform(
  imageWidth: number,
  imageHeight: number,
  focalPoint: FocalPoint
): string {
  const scale = calculateScale(imageWidth, imageHeight, focalPoint);
  const translateX = transX(imageWidth, focalPoint.centroid_x);
  const translateY = transY(imageHeight, focalPoint.centroid_y);

  return `scale(${scale}) translate(${translateX}, ${translateY})`;
}
